/**
 * WorkflowCanvas — React Flow + dagre visualization of workflow steps
 * Shows directed graph: nodes = skills, edges = success/failure paths
 */
import React, { useMemo, useState, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

// ─── Types ────────────────────────────────────────────────

interface WorkflowStep {
  step: number;
  skill: string;
  on_success: number | null;
  failure_mode?: string;
  conditions?: { field: string; value: any; operator: string; next_step: number }[];
  route_human_review?: any;
}

interface WorkflowCanvasProps {
  workflowCode: string;
  workflowName: string;
  department: string;
  steps: WorkflowStep[];
  onClose: () => void;
}

// ─── Engine color mapping ─────────────────────────────────

const ENGINE_COLORS: Record<string, string> = {
  CALCULATE: 'var(--accent)', DETECT: '#F59E0B', VALIDATE: '#10B981',
  FORMAT: '#A78BFA', MATCH: '#FB923C', RESEARCH: '#EC4899',
  NOTIFY: '#F87171', ROUTE: '#38BDF8', INGEST: '#34D399', RESOLVE: '#FBBF24',
};

function getSkillCategory(skillCode: string): string {
  const prefix = skillCode.split('_')[0];
  if (ENGINE_COLORS[prefix]) return prefix;
  if (skillCode.startsWith('DETECT')) return 'DETECT';
  if (skillCode.startsWith('VALIDATE')) return 'VALIDATE';
  if (skillCode.startsWith('RESOLVE')) return 'RESOLVE';
  if (skillCode.startsWith('CALCULATE') || skillCode.startsWith('CALC')) return 'CALCULATE';
  if (skillCode.startsWith('FORMAT')) return 'FORMAT';
  if (skillCode.startsWith('MATCH')) return 'MATCH';
  if (skillCode.startsWith('INGEST')) return 'INGEST';
  if (skillCode.startsWith('NOTIFY')) return 'NOTIFY';
  if (skillCode.startsWith('ROUTE')) return 'ROUTE';
  if (skillCode.startsWith('RESEARCH')) return 'RESEARCH';
  return 'SKILL';
}

function humanizeSkillCode(code: string): string {
  return code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── dagre layout ─────────────────────────────────────────

const NODE_WIDTH = 200;
const NODE_HEIGHT = 80;
const END_SIZE = 56;
const HUMAN_SIZE = 56;

function computeLayout(steps: WorkflowStep[]) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 120, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));

  // Add step nodes
  for (const s of steps) {
    g.setNode(`s${s.step}`, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }

  // Add END node
  g.setNode('END', { width: END_SIZE, height: END_SIZE });

  // Track if we need a HUMAN_REVIEW node
  let hasHumanReview = false;

  // Add edges
  for (const s of steps) {
    // Success path
    if (s.on_success !== null && s.on_success !== undefined) {
      g.setEdge(`s${s.step}`, `s${s.on_success}`, { label: 'success' });
    } else {
      // null = END
      g.setEdge(`s${s.step}`, 'END', { label: 'success' });
    }

    // Conditional branches
    if (s.conditions) {
      for (const cond of s.conditions) {
        if (cond.next_step) {
          g.setEdge(`s${s.step}`, `s${cond.next_step}`, { label: 'condition' });
        }
      }
    }

    // Human review gate
    if (s.route_human_review) {
      if (!hasHumanReview) {
        g.setNode('HUMAN', { width: HUMAN_SIZE, height: HUMAN_SIZE });
        hasHumanReview = true;
      }
      g.setEdge(`s${s.step}`, 'HUMAN', { label: 'human_review' });
    }
  }

  dagre.layout(g);
  return { graph: g, hasHumanReview };
}

// ─── Custom nodes ─────────────────────────────────────────

interface SkillNodeData {
  stepNumber: number;
  skillCode: string;
  skillName: string;
  category: string;
  categoryColor: string;
  failureMode?: string;
  hasConditions: boolean;
  hasHumanGate: boolean;
  onSelect: (data: SkillNodeData) => void;
  [key: string]: any;
}

function SkillNode({ data }: { data: SkillNodeData }) {
  return (
    <div
      onClick={() => data.onSelect(data)}
      style={{
        background: 'var(--bg-surface, var(--bg-surface))',
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeft: `3px solid ${data.categoryColor}`,
        borderRadius: 8,
        padding: '10px 14px',
        width: NODE_WIDTH,
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600, color: data.categoryColor, marginBottom: 4 }}>
        {data.category}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary, #F0F2F5)', marginBottom: 4, lineHeight: 1.3 }}>
        {data.skillName}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 9, color: 'var(--text-hint, #475569)' }}>Step #{data.stepNumber}</span>
        {data.hasHumanGate && (
          <span style={{ fontSize: 8, padding: '1px 5px', background: '#fffbeb', color: '#fcd34d', borderRadius: 3, fontWeight: 600 }}>GATE</span>
        )}
        {data.failureMode === 'CONTINUE' && (
          <span style={{ fontSize: 8, padding: '1px 5px', background: 'rgba(100,116,139,0.15)', color: '#94a3b8', borderRadius: 3 }}>CONTINUE</span>
        )}
      </div>
      <Handle type="target" position={Position.Left} style={{ background: 'var(--border-subtle)', border: 'none', width: 6, height: 6 }} />
      <Handle type="source" position={Position.Right} style={{ background: 'var(--border-subtle)', border: 'none', width: 6, height: 6 }} />
    </div>
  );
}

function EndNode() {
  return (
    <div style={{
      background: 'var(--bg-elevated, var(--bg-elevated))',
      border: '1px solid rgba(16,185,129,0.3)',
      borderRadius: '50%',
      width: END_SIZE, height: END_SIZE,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 600, color: '#10B981',
    }}>
      END
      <Handle type="target" position={Position.Left} style={{ background: 'transparent', border: 'none' }} />
    </div>
  );
}

function HumanNode() {
  return (
    <div style={{
      background: 'var(--bg-elevated, var(--bg-elevated))',
      border: '1px solid rgba(245,158,11,0.3)',
      borderRadius: '50%',
      width: HUMAN_SIZE, height: HUMAN_SIZE,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9, fontWeight: 600, color: '#fcd34d', textAlign: 'center' as const,
      lineHeight: 1.2,
    }}>
      HUMAN<br/>REVIEW
      <Handle type="target" position={Position.Left} style={{ background: 'transparent', border: 'none' }} />
    </div>
  );
}

const nodeTypes = { skill: SkillNode, end: EndNode, human: HumanNode };

// ─── Main component ───────────────────────────────────────

export default function WorkflowCanvas({ workflowCode, workflowName, department, steps, onClose }: WorkflowCanvasProps) {
  const [selectedNode, setSelectedNode] = useState<SkillNodeData | null>(null);

  const handleNodeSelect = useCallback((data: SkillNodeData) => {
    setSelectedNode(prev => prev?.stepNumber === data.stepNumber ? null : data);
  }, []);

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!steps || steps.length === 0) return { initialNodes: [], initialEdges: [] };

    const { graph, hasHumanReview } = computeLayout(steps);

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Convert dagre nodes to React Flow nodes
    for (const s of steps) {
      const nodeId = `s${s.step}`;
      const pos = graph.node(nodeId);
      if (!pos) continue;
      const category = getSkillCategory(s.skill);
      nodes.push({
        id: nodeId,
        type: 'skill',
        position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
        data: {
          stepNumber: s.step,
          skillCode: s.skill,
          skillName: humanizeSkillCode(s.skill),
          category,
          categoryColor: ENGINE_COLORS[category] || '#64748B',
          failureMode: s.failure_mode,
          hasConditions: !!s.conditions?.length,
          hasHumanGate: !!s.route_human_review,
          onSelect: handleNodeSelect,
        },
      });
    }

    // END node
    const endPos = graph.node('END');
    if (endPos) {
      nodes.push({
        id: 'END',
        type: 'end',
        position: { x: endPos.x - END_SIZE / 2, y: endPos.y - END_SIZE / 2 },
        data: {},
      });
    }

    // HUMAN node
    if (hasHumanReview) {
      const humanPos = graph.node('HUMAN');
      if (humanPos) {
        nodes.push({
          id: 'HUMAN',
          type: 'human',
          position: { x: humanPos.x - HUMAN_SIZE / 2, y: humanPos.y - HUMAN_SIZE / 2 },
          data: {},
        });
      }
    }

    // Convert dagre edges to React Flow edges
    const dagreEdges = graph.edges();
    for (const e of dagreEdges) {
      const edgeData = graph.edge(e);
      const isFailure = edgeData?.label === 'condition';
      const isHuman = edgeData?.label === 'human_review';

      edges.push({
        id: `${e.v}-${e.w}`,
        source: e.v,
        target: e.w,
        type: 'smoothstep',
        style: {
          stroke: isHuman ? '#F59E0B' : isFailure ? '#64748B' : '#10B981',
          strokeWidth: isHuman ? 1.5 : 2,
          strokeDasharray: isHuman ? '6 4' : isFailure ? '4 3' : undefined,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isHuman ? '#F59E0B' : isFailure ? '#64748B' : '#10B981',
          width: 14,
          height: 14,
        },
        label: isHuman ? 'review' : isFailure ? 'fallback' : undefined,
        labelStyle: { fontSize: 9, fill: isHuman ? '#F59E0B' : '#64748B' },
        labelBgStyle: { fill: 'var(--bg-base, var(--bg-base))', fillOpacity: 0.8 },
      });
    }

    return { initialNodes: nodes, initialEdges: edges };
  }, [steps, handleNodeSelect]);

  const [rfNodes, , onNodesChange] = useNodesState(initialNodes);
  const [rfEdges, , onEdgesChange] = useEdgesState(initialEdges);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', minHeight: 500 }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        background: 'var(--bg-surface, var(--bg-surface))', borderBottom: '1px solid var(--border-subtle, rgba(0,0,0,0.06))',
        borderRadius: 'var(--radius-md, 8px) var(--radius-md, 8px) 0 0',
      }}>
        <button onClick={onClose} style={{
          background: 'var(--bg-elevated, var(--bg-elevated))', border: '1px solid var(--border-default, rgba(0,0,0,0.06))',
          borderRadius: 6, padding: '5px 12px', fontSize: 11, color: 'var(--text-secondary, #94A3B8)',
          cursor: 'pointer', fontWeight: 500,
        }}>
          &larr; Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #F0F2F5)' }}>{workflowName}</div>
          <div style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-hint, #475569)', marginTop: 1 }}>
            {workflowCode} &middot; {department.replace(/_/g, ' ')} &middot; {steps.length} steps
          </div>
        </div>
        {/* Legend */}
        <div style={{ display: 'flex', gap: 12, fontSize: 9, color: 'var(--text-hint, #475569)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 16, height: 2, background: '#10B981', borderRadius: 1 }} /> success
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 16, height: 2, background: '#64748B', borderRadius: 1, border: '1px dashed #64748B' }} /> fallback
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 16, height: 2, background: '#F59E0B', borderRadius: 1, border: '1px dashed #F59E0B' }} /> review
          </span>
        </div>
      </div>

      {/* Canvas + side panel */}
      <div style={{ flex: 1, display: 'flex', borderRadius: '0 0 var(--radius-md, 8px) var(--radius-md, 8px)', overflow: 'hidden', border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))', borderTop: 'none' }}>
        <div style={{ flex: 1 }}>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            proOptions={{ hideAttribution: true }}
            style={{ background: 'var(--bg-base, var(--bg-base))' }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
          >
            <Background color="rgba(0,0,0,0.02)" gap={24} />
            <MiniMap
              style={{ background: 'var(--bg-surface, var(--bg-surface))', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 6 }}
              nodeColor={(node: Node) => (node.data as any)?.categoryColor || '#64748B'}
              maskColor="rgba(10, 22, 40, 0.7)"
            />
            <Controls
              style={{ background: 'var(--bg-surface, var(--bg-surface))', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 6 }}
            />
          </ReactFlow>
        </div>

        {/* Side panel — node detail */}
        {selectedNode && (
          <div style={{
            width: 260, borderLeft: '1px solid var(--border-subtle, rgba(0,0,0,0.06))',
            padding: 16, background: 'var(--bg-surface, var(--bg-surface))', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600, color: selectedNode.categoryColor }}>
                {selectedNode.category}
              </span>
              <button onClick={() => setSelectedNode(null)} style={{
                background: 'none', border: 'none', color: 'var(--text-hint, #475569)',
                cursor: 'pointer', fontSize: 14, lineHeight: 1,
              }}>&times;</button>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #F0F2F5)', marginBottom: 4 }}>
              {selectedNode.skillName}
            </div>
            <div style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-hint, #475569)', marginBottom: 12 }}>
              {selectedNode.skillCode}
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.06))', paddingTop: 12 }}>
              <DetailRow label="Step" value={`#${selectedNode.stepNumber}`} />
              <DetailRow label="Engine" value={selectedNode.category} color={selectedNode.categoryColor} />
              <DetailRow label="Failure Mode" value={selectedNode.failureMode || 'HALT'} />
              {selectedNode.hasConditions && <DetailRow label="Branching" value="Yes (conditional)" color="#A78BFA" />}
              {selectedNode.hasHumanGate && <DetailRow label="Human Gate" value="Threshold review" color="#F59E0B" />}
            </div>
          </div>
        )}
      </div>

      {/* React Flow dark theme overrides */}
      <style>{`
        .react-flow__controls-button {
          background: var(--bg-surface, var(--bg-surface)) !important;
          color: var(--text-secondary, #94A3B8) !important;
          border-color: rgba(255,255,255, 0.06) !important;
          fill: var(--text-secondary, #94A3B8) !important;
        }
        .react-flow__controls-button:hover {
          background: var(--bg-elevated, var(--bg-elevated)) !important;
        }
        .react-flow__controls-button svg {
          fill: var(--text-secondary, #94A3B8) !important;
        }
        .react-flow__edge-textbg { rx: 4; }
      `}</style>
    </div>
  );
}

function DetailRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted, #64748B)' }}>{label}</span>
      <span style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)', fontWeight: 500, color: color || 'var(--text-primary, #F0F2F5)' }}>{value}</span>
    </div>
  );
}
